import { Address } from "@app/model/Address";
import { format } from "date-fns";

export const dateFormater = (date: Date) =>
        date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

export const numberOfDays = (startDate: Date, endDate: Date) => {
    if (!startDate || !endDate) return 0;

    // 2. Convert the incoming string (or Date) into a guaranteed JS Date object
    const start = new Date(startDate);
    const end = new Date(endDate);
    // In JavaScript, simply subtracting two dates can cause bugs if a Daylight Saving Time (DST) 
    // transition happens between them (making a day 23 or 25 hours long).
    //To safely ignore the time and avoid DST bugs, force both dates to UTC midnight before calculating the difference.
    const d1 = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const d2 = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

    // Define how many milliseconds are in one day
    const msPerDay = 1000 * 60 * 60 * 24;

    // Calculate the difference and convert to days
    return Math.abs((d2 - d1) / msPerDay) + 1; // +1 to include both start and end dates in the count
}

/**
 * A Date as the "yyyy-MM-dd" the API takes, read off the calendar the user is looking at.
 *
 * Not toISOString(): that converts to UTC first, so a local date turns into the day
 * before for any positive offset.
 */
export const toIsoDate = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * The reverse, as a *local* date.
 *
 * Not new Date(iso): a date-only string is defined to parse as midnight UTC, which every
 * formatter then renders as the day before in any negative offset - so a date picked as
 * 01/01 reads back as 31/12. Use this wherever a stored "yyyy-MM-dd" is shown.
 */
export const fromIsoDate = (iso: string): Date => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
};

/** "dd/MM/yyyy" as a local date, for PatternFly's DatePicker dateParse. Pair it with
 * dateFormater, which renders the same format. */
export const ddMMyyyyParse = (value: string): Date => {
    const [day, month, year] = value.split('/').map(Number);
    if (!day || !month || !year) return new Date(value);
    return new Date(year, month - 1, day);
};

export const dateParser = (date: string) => {
    if (date == null || date.trim().length === 0){
        return new Date();
    }
    const split = date.split('-');
    if (split.length !== 3) {
        return new Date();
    }
    const day = split[0];
    const month = split[1];
    const year = split[2];
    return new Date(`${year?.padStart(4, '0')}-${month?.padStart(2, '0')}-${day?.padStart(2, '0')}T00:00:00`);
};

/**
 * Parses "dd/mm/yyyy hh:mm".
 *
 * Accepts either separator in the date. It used to split on a hyphen alone, which no caller ever
 * produced - dateFormater writes slashes - so every call fell through to the `new Date()` below and
 * returned the current moment instead of the date it was given. Silent, and wrong in the one way
 * that looks plausible on screen.
 *
 * Note what the fallbacks mean: an input this cannot read comes back as *now*, not as an invalid
 * date, so a caller cannot tell a parse failure from a deliberate "now". Prefer building a Date
 * directly where you can, as DateTimePicker now does.
 */
export const dateTimeParser = (dateTime: string) => {
    const split1 = dateTime.split(' ');
    if (split1.length !== 2) {
        return new Date();
    }
    const date = split1[0];
    const time = split1[1];
    if (!date) {
        return new Date();
    }
    const splitDate = date.split(/[/-]/);
    if (splitDate.length !== 3) {
        return new Date();
    }
    const day = splitDate[0];
    const month = splitDate[1];
    const year = splitDate[2];
    return new Date(`${year?.padStart(4, '0')}-${month?.padStart(2, '0')}-${day?.padStart(2, '0')}T${time}:00`);
};

export function ddMMyyyyFormat(date: string | Date | undefined | null) {
    if ( !date || date == null || (typeof date === 'string' && date.trim().length === 0)){
        return "";
    }
    const _date = typeof date === 'string' ? new Date(date) : date;
    return format(_date, "dd/MM/yyyy");
}

export function HHmmFormat(date: string | Date | undefined | null) {
    if ( !date || date == null || (typeof date === 'string' && date.trim().length === 0)){
        return "";
    }
    const _date = typeof date === 'string' ? new Date(date) : date;
    return format(_date, "HH:mm")
}

export function ddMMyyyyHHmmFormat(date: string | Date | undefined | null) {
    if ( !date || date == null || (typeof date === 'string' && date.trim().length === 0)){
        return "";
    }
    return ddMMyyyyFormat(date) + " " + HHmmFormat(date);
}

export function ddMMyyyyHHmmTzFormat(date: string | Date | undefined | null) {
    if ( !date || date == null || (typeof date === 'string' && date.trim().length === 0)){
        return "";
    }
    return ddMMyyyyHHmmFormat(date) + ' ' + format(date, 'zzz');
}

/**
 * The ISO 8601 week of the year, 1-53.
 *
 * ISO weeks run Mon-Sun and week 1 is the one holding the first Thursday, so the opening days of
 * January can still belong to week 52 or 53 of the year before. date-fns spells this `I`. The
 * lowercase `w` that is easy to reach for instead is the *locale's* week, which under the default
 * en-US locale runs Sun-Sat and always puts Jan 1 in week 1 - a different number for the same day,
 * and not the one the History endpoint filters on.
 *
 * A number rather than the string `format` hands back, so that the Week column sorts 9 before 10.
 */
export function weekNumber(date: string | Date) {
    return Number(format(date, "I"));
}

/**
 * An ISO 4217 code as a reader should see it: `INR` reads "Indian Rupee".
 *
 * Asked of Intl rather than of a table of our own. A class carries whatever code the service was
 * sent - it holds a `java.util.Currency`, so any of the standard's codes can arrive - and a table
 * here would answer for the handful we have seen and throw on the rest.
 *
 * An unrecognised code reads as itself, which is the honest answer, and a malformed one - anything
 * Intl will not even look up - is caught rather than thrown out of a render.
 */
export const currencyName = (code: string | undefined | null): string => {
    if (!code) return "";
    try {
        return new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) ?? code;
    } catch {
        return code;
    }
};

/**
 * An amount in its currency: "$12,345.00", "₹12,345.00".
 *
 * Intl's job rather than a symbol lookup and a concatenation, because the number of decimals is
 * the currency's own: JPY has no minor unit, and a hand-rolled formatter would write "¥12,345.00"
 * for an amount that has no pennies to show. `narrowSymbol` keeps it to "$" where the locale
 * would otherwise disambiguate to "US$".
 *
 * Empty when there is no amount. An amount whose currency is missing or unusable is still worth
 * showing, so it is rendered as a plain grouped number rather than dropped.
 */
export const formatMoney = (amount: number | undefined | null, code?: string | null): string => {
    if (amount === undefined || amount === null) return "";
    const plain = new Intl.NumberFormat('en-GB').format(amount);
    if (!code) return plain;
    try {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: code,
            currencyDisplay: 'narrowSymbol',
        }).format(amount);
    } catch {
        return `${code} ${plain}`;
    }
};

export function displayAddress (address : Address) {
    if (!address) {
        return "";
    }
    let addressFormated = (address.line1 ? address.line1 + ", " : "");
    addressFormated += (address.line2 ? address.line2 + ", " : "");
    addressFormated += (address.line3 ? address.line3 + ", " : "");
    addressFormated += (address.city ? address.city + ", " : "");
    addressFormated += (address.postcode ? address.postcode + ", " : "");
    addressFormated += (address.country ? address.country.name : "");

    addressFormated = addressFormated.trim();
    if (addressFormated.endsWith(',')){
        addressFormated = addressFormated.substring(0, addressFormated.length-1);
    }
    return addressFormated;
}

export function getBoolean(value: any){
    switch(value){
        case true:
        case "true":
        case 1:
        case "1":
        case "on":
        case "yes":
            return true;
        default: 
            return false;
    }
}

export const getSundayXWeeksAgo = (numberOfWeeks : number): string => {
  const d = new Date();

  // d.getDay() gets us back to THIS week's Sunday.
  // Then a whole number of weeks back from there.
  const daysToSubtract = d.getDay() + (numberOfWeeks * 7);

  d.setDate(d.getDate() - daysToSubtract);

  // Formats to "YYYY-MM-DD" for standard API consumption, e.g. "2026-06-28". Off the
  // local calendar: toISOString() would name the day before whenever the offset pushes
  // the current time past midnight UTC.
  return toIsoDate(d);
};